import * as sdk from 'microsoft-cognitiveservices-speech-sdk';
import {PropertyId, SpeechRecognizer, TranslationRecognizer} from 'microsoft-cognitiveservices-speech-sdk';
import {TranslationProcessor} from './TranslationProcessor';
import {
    Input,
    Language,
    Output,
    RawSpeakRecognitionL,
    RawText2SpeechL,
    RawTranslationL,
    RecognizableAndTranslatableLanguage,
    TranslatableAndSpeakableLanguage,
    TranslationResult,
    TranslationsBody
} from './Configuration';
import {Text2SpeechProcessor} from './Text2SpeechProcessor';

// import untypedSpeakRecognitionLanguages from '../SpeakRecognitionSupportedLanguages_small.json';
import untypedSpeakRecognitionLanguages from '../SpeakRecognitionSupportedLanguages_V1.json';
// import untypedText2SpeechLanguages from '../Text2SpeechSupportedLangua/**/ges_small.json';
import untypedText2SpeechLanguages from '../Text2SpeechSupportedLanguages_V1.json';
import untypedTranslationLanguages from '../translationLanguages.json';
import {CancellationReason} from 'microsoft-cognitiveservices-speech-sdk/distrib/lib/src/sdk/Exports';
import {UUID} from 'crypto';

const azureRegion = 'eastus';

export class SessionContext {

    public static currentRecognizedTokens: string[] | null = null;
    private sessionId: UUID;
    private modelType: string;
    private selectedOutputs: Output[];
    private selectedInput: Input;
    private inputLangKey: string;

    private readonly speechRecognitionConfig: sdk.SpeechTranslationConfig;

    private speechRecognizer: SpeechRecognizer | undefined;
    private translationRecognizer: TranslationRecognizer | undefined;

    //private translationProcessor: TranslationProcessor;
    private speech2TextProcessorByLanguage: Map<string, Text2SpeechProcessor>;
    private state: 'READY' | 'RUNNING' | 'STOPPED';

    public setSpeechToken(newToken: string) {
        console.log('Refreshing Azure Token... Text 2 Speech');
        this.speech2TextProcessorByLanguage?.forEach(processor => {
            processor.setSpeechToken(newToken);
        });
        console.log('Refresh Successful Text 2 Speech');


        if (this.speechRecognizer) {
            console.log('Refreshing Azure Token... speechRecognizer');
            this.speechRecognizer.authorizationToken = newToken;
            console.log('Refresh Successful speechRecognizer');
        }

        if (this.translationRecognizer) {
            console.log('Refreshing Azure Token... translationRecognizer');
            this.translationRecognizer.authorizationToken = newToken;
            console.log('Refresh Successful translationRecognizer');
        }
    }

    constructor(modelType: string, azureSpeechAPIToken: string, sessionId: UUID, selectedInput: Input, selectedOutputs: Output[],
                onPartialRecognitionReady: (recognizedText: string) => void,
                onRecognitionReady: (recognizedText: string) => void,
                onPartialTranslationReady: (translationReady: Map<Language, TranslationResult>) => void,
                onTranslationReady: (translationBody: TranslationsBody) => void,
                onSessionCanceled: (sessionId: UUID, azureSessionId: string, reason: CancellationReason) => void) {

        this.modelType = modelType;
        this.sessionId = sessionId;
        this.selectedInput = selectedInput;
        this.inputLangKey = selectedInput.language.recognitionKey.split('-')[0];
        this.selectedOutputs = selectedOutputs;

        console.log('[Session Context] Constructor')
        console.log('[Session Context] Selected Input', this.selectedInput)
        console.log('[Session Context] Selected Output', this.selectedOutputs)

        /*this.translationProcessor = this.startTranslationProcessor(
            translationKey,
            this.selectedOutputs.map(output => output.language),
            this.selectedInput.language,
            onTranslationReady
        );*/

        console.log('[Session Context] Starting Text2Speech processors...');
        this.speech2TextProcessorByLanguage = new Map<string, Text2SpeechProcessor>();
        this.selectedOutputs.forEach(output => {
            this.speech2TextProcessorByLanguage.set(output.language.translationKey, this.startText2SpeechProcessor(output, azureSpeechAPIToken));
            console.log('[Session Context] Text2Speech processor started for lang key: ', output.language.speakKey);
        });
        console.log('[Session Context] Completed Text2Speech processors...');

        //const speechConfig = sdk.SpeechConfig.fromAuthorizationToken(azureSubscriptionKey, azureRegion);
        console.log('[Session Context] Starting Voice Recognition and Translation processors...');
        const audioConfig = sdk.AudioConfig.fromMicrophoneInput(this.selectedInput.device.deviceId);

        this.speechRecognitionConfig = sdk.SpeechTranslationConfig.fromAuthorizationToken(azureSpeechAPIToken, azureRegion);
        //this.speechRecognitionConfig.enableDictation();
        this.speechRecognitionConfig.setProperty(PropertyId.Speech_SegmentationSilenceTimeoutMs, '400');

        //Pame
        // this.speechRecognitionConfig.voiceName = this.selectedOutputs[0].language.voices[0];

        this.speechRecognitionConfig.speechRecognitionLanguage = this.selectedInput.language.recognitionKey;

        this.selectedOutputs.forEach(output => {
            console.log('[Session Context] Adding translation target language: ', output);
            this.speechRecognitionConfig.addTargetLanguage(output.language.translationKey);
        });

        // if (selectedInput.autoDetect) {
        //     const autoDetectSourceLanguageConfig = sdk.AutoDetectSourceLanguageConfig.fromLanguages(
        //         ["en-US", "de-DE", "zh-CN"] //TODO fix lista
        //     );
        //
        //     // this.speechRecognitionConfig.speechRecognitionLanguage = "en-US";
        //     console.log("-----------------------")
        //     console.log("autoDetectSourceLanguageConfig.properties", autoDetectSourceLanguageConfig.properties)
        //     console.log("speechTranslationConfigImpl.properties", (this.speechRecognitionConfig as SpeechTranslationConfigImpl).properties)
        //
        //
        //     /*
        //         const speechTranslationConfigImpl: SpeechTranslationConfigImpl = speechTranslationConfig as SpeechTranslationConfigImpl;
        //         autoDetectSourceLanguageConfig.properties.mergeTo(speechTranslationConfigImpl.properties);
        //         return new TranslationRecognizer(speechTranslationConfig, audioConfig);
        //     */
        //     this.speechRecognizer = sdk.TranslationRecognizer.FromConfig(this.speechRecognitionConfig, autoDetectSourceLanguageConfig, audioConfig)
        //
        //
        // } else {
        this.speechRecognitionConfig.speechRecognitionLanguage = this.selectedInput.language.recognitionKey; //en-US
        if (this.selectedOutputs.length === 0) { //Text Only

            this.speechRecognizer = new sdk.SpeechRecognizer(this.speechRecognitionConfig, audioConfig);

            this.speechRecognizer.recognizing = (sender, event) => {
                let partiallyRecognizedText = event.result.text;
                onPartialRecognitionReady(partiallyRecognizedText);
            }

            this.speechRecognizer.recognized = (sender, event) => {
                //Stable recognition - execute recognitionReady callback
                let recognizedText = event.result.text;
                onRecognitionReady(recognizedText);
            }

            this.speechRecognizer.canceled = (sender, event) => {
                onSessionCanceled(this.sessionId, event.sessionId, event.reason);
            }

        } else {
            this.translationRecognizer = new sdk.TranslationRecognizer(this.speechRecognitionConfig, audioConfig);

            //Handle unstable recognition
            this.translationRecognizer.recognizing = (sender, event) => {
                let partiallyRecognizedText = event.result.text;
                onPartialRecognitionReady(partiallyRecognizedText);

                let parsedTranslations = new Map<Language, TranslationResult>();
                event.result.translations.languages.forEach(translationKey => {
                        let partialTranslation = event.result.translations.get(translationKey);
                        const lang = {id: translationKey} as Language;
                        // console.log("SENT raw 2", tr)
                        const translationResult = {
                            originalText: partiallyRecognizedText || '',
                            translatedText: partialTranslation,
                            status: '',
                            language: lang
                        } as TranslationResult;
                        parsedTranslations.set(lang, translationResult);

                        // SessionContext.processPartialRecognition(partialTranslation, text => {
                        //     this.speech2TextProcessorByLanguage?.get(translationResult.language.id)?.enqueueText(text);
                        // })

                        if ('onlyStableModel' !== modelType && 'ur' !== translationKey) {
                            console.log('[Sending PARTIAL to speech2TextProcessorByLanguage -> ', translationResult.language.id, this.speech2TextProcessorByLanguage)
                            this.speech2TextProcessorByLanguage?.get(translationResult.language.id)?.processPartialRecognition(partialTranslation, text => {
                                this.speech2TextProcessorByLanguage?.get(translationResult.language.id)?.enqueueText(text);
                            });
                        }
                    }
                )

                onPartialTranslationReady(parsedTranslations);
            }

            //When recognition is ready do something
            this.translationRecognizer.recognized = (sender, event) => {

                //Stable recognition - execute recognitionReady callback
                let recognizedText = event.result.text;
                onRecognitionReady(recognizedText);

                //Get the translations
                let parsedTranslations = new Map<Language, TranslationResult>();
                let translations: TranslationsBody = {inputs: {}, outputs: {}};
                event.result.translations.languages.forEach(translationKey => {
                        let partialTranslation = event.result.translations.get(translationKey);
                        const lang = {id: translationKey} as Language;
                        const translationResult = {
                            originalText: recognizedText || '',
                            translatedText: partialTranslation,
                            status: '',
                            language: lang
                        } as TranslationResult;

                        //Build a list of all translations by language
                        parsedTranslations.set(lang, translationResult);
                        translations.outputs[translationKey] = partialTranslation;
                        if (!translations.inputs[this.inputLangKey])
                            translations.inputs[this.inputLangKey] = recognizedText;


                        //Enqueue to Text 2 Speech
                        // SessionContext.processPartialRecognition(partialTranslation, text => {
                        //     this.speech2TextProcessorByLanguage?.get(translationResult.language.id)?.enqueueText(text);
                        // })
                        console.log('[Sending STABLE to speech2TextProcessorByLanguage -> ', translationResult.language.id, this.speech2TextProcessorByLanguage)

                        this.speech2TextProcessorByLanguage?.get(translationResult.language.id)?.processRecognition(this.modelType, partialTranslation, text => {
                            this.speech2TextProcessorByLanguage?.get(translationResult.language.id)?.enqueueText(text);
                        });
                    }
                )

                SessionContext.currentRecognizedTokens = [];

                //Stable translation - execute recognitionReady callback
                onTranslationReady(translations);
            }

            // this.speechRecognizer.synthesizing = (sender, event) => {
            //     console.log('[synthesizing]', event.result.reason);
            //     this.speech2TextProcessorByLanguage?.get(this.selectedOutputs[0].language.translationKey)?.sendToDevice(event.result.audio);
            // }


            //para testear
            this.translationRecognizer.sessionStarted = (sender, event) => {
                console.log('[Session Context] Voice Recognition and Translation processors started. Event -> ', event);
            }
            this.translationRecognizer.sessionStopped = (sender, event) => {
                console.log('[sessionStopped]...', event);
            }
            this.translationRecognizer.canceled = (sender, event) => {
                console.error('[Session Context] Voice Recognition and Translation processors canceled...', event);
                onSessionCanceled(this.sessionId, event.sessionId, event.reason);
            }
        }
        // }

        this.state = 'READY';
        console.log('[Session Context] All processors are installed. Session context is ready to start recognition.');
    }

    public static processPartialRecognition(partiallyRecognizedText: string, onRecognitionReady: (recognizedText: string) => void) {
        if (!SessionContext.currentRecognizedTokens)
            SessionContext.currentRecognizedTokens = [];

        let length = SessionContext.currentRecognizedTokens.length;

        const tokens = partiallyRecognizedText.match(/[^.;?!,]+[.;?!,]?/g);

        if (!tokens) return;
        //hola. i 0 l 1
        //hola. dada i 0 i 1 l 2
        for (let i = 0; i < (tokens?.length || 0); i++) {
            const token = tokens[i];

            // Check if the token at this index has been processed before
            if (!SessionContext.currentRecognizedTokens[i]) {

                if (!['.', ';', '?', '!', ','].includes(token.charAt(token.length - 1))) return;
                // Process the token using translationProcessor
                //this.translationProcessor.enqueueText(token);
                // Add the token's index to the map
                SessionContext.currentRecognizedTokens.push(token);
                //console.log("Pushing token: ", token)
                onRecognitionReady(token);
            }
        }
    }

    public start() {
        //this.translationProcessor.start();
        this.speech2TextProcessorByLanguage?.forEach(processor => {
            processor.start()
        });
        this.speechRecognizer?.startContinuousRecognitionAsync();
        this.translationRecognizer?.startContinuousRecognitionAsync();
        this.state = 'RUNNING';
        console.log('[Session Context] Started...');

        // return new Promise<void>(() => {});
    }

    public stop() {
        console.log('[Session Context] [2] Stop ')
        this.speechRecognizer?.stopContinuousRecognitionAsync();
        this.speechRecognizer?.close();
        this.translationRecognizer?.stopContinuousRecognitionAsync();
        this.translationRecognizer?.close();
        this.speech2TextProcessorByLanguage?.forEach((processor, index) => {
            console.log('[Session Context] [3] Stopping text2speech processor: ' + processor.getOutputConfig() + ' + (' + index + ')')
            processor.stop();
        })
        //this.translationProcessor.stop();
        this.state = 'STOPPED';
        console.log('[Session Context] [5] Stopped')
    }

    public getState() {
        return this.state;
    }

    private startTranslationProcessor = (translationKey: string,
                                         outputLanguages: TranslatableAndSpeakableLanguage[],
                                         inputLanguage: RecognizableAndTranslatableLanguage,
                                         onTranslationReady: (translationsReady: Map<Language, TranslationResult>) => void
    ): TranslationProcessor => {

        return new TranslationProcessor(translationKey, inputLanguage, outputLanguages,
            (_translationsReady) => {
                _translationsReady.forEach(translation => {
                    console.log('[Session Context] Translation queuing into Text2Speech: ', translation);
                    console.log('[Session Context] Processors: ', this.speech2TextProcessorByLanguage);
                    this.speech2TextProcessorByLanguage?.get(translation.language.id)?.enqueueText(translation.translatedText);
                })
                onTranslationReady(_translationsReady);
            });
    }

    private startText2SpeechProcessor = (outputConfig: Output, azureSpeechAPIToken: string)
        /*onSpeechAudioReady: (speechAudio: { lang: string, text: string }[]) => void,
        onSpeechAudioPlaybackCompleted: (speechAudio: { lang: string, text: string }[]) => void*/
        : Text2SpeechProcessor => {
        return new Text2SpeechProcessor(outputConfig, azureSpeechAPIToken);
    }

    public getText2Speech(translationKey: string) {
        return this.speech2TextProcessorByLanguage?.get(translationKey);
    }
}

export const loadAvailableLanguages = async () => {
    //GET https://api.cognitive.microsofttranslator.com/Languages?api-version=3.0&scope=translation
    const uri = 'https://api.cognitive.microsofttranslator.com/Languages?api-version=3.0&scope=translation';
    const r = await fetch(uri, {method: 'get'})
        .then(response => response.json())
        .catch(error => {
            console.log('--> error:', error);
            return {translation: new Map<String, Language>()}
        });
    console.log('---> Languages:', r.translation);

    const languages: Language[] = Object.keys(r.translation)
        .map(langKey => ({
            id: langKey,
            name: r.translation[langKey].name,
            nativeName: r.translation[langKey].nativeName
        }));
    console.log('languageMap: ', languages);
    return languages;
    // state.availableLanguages = languages;
}

export const buildLanguageMatrixV2 = async () => {
    // const uri = 'https://api.cognitive.microsofttranslator.com/Languages?api-version=3.0&scope=translation';
    // const response: RawTranslationL = await fetch(uri, {method: 'get'})
    //     .then(response => response.json())
    //     .catch(error => {
    //         console.log('--> error:', error);
    //         return {translation: new Map<String, Language>()}
    //     });

    const supportedRecognitionJson: RawSpeakRecognitionL = untypedSpeakRecognitionLanguages; //"am-ET": "Amharic (Ethiopia)"
    const supportedSpeakableJson: RawText2SpeechL = untypedText2SpeechLanguages; //"am-ET": {"label":"Amharic (Ethiopia)", "voices": ["am-ET-MekdesNeural2","am-ET-AmehaNeural2"]},

    //
    const supportedRecognition = Object.keys(supportedRecognitionJson);//["am-ET", ...]
    const supportedTranslations = untypedTranslationLanguages.translation;
    const supportedSpeakable = Object.keys(supportedSpeakableJson);//["am-ET", ...]

    const recognizableAndTranslatable: RecognizableAndTranslatableLanguage[] = [];
    const translatableAndSpeakable: TranslatableAndSpeakableLanguage[] = [];


    supportedRecognition.forEach(recognitionKey => {
        const recognition = supportedRecognitionJson[recognitionKey];
        recognizableAndTranslatable.push({
            name: recognition,
            recognitionKey: recognitionKey
        });
    });

    Object.keys(supportedTranslations).forEach(translationKey => {

        //zh-Hans     zh-HK  zh-NR
        //zh-Hant     zh
        if ('zh-Hans' === translationKey) {
            const key = 'zh-CN';
            translatableAndSpeakable.push({
                name: supportedSpeakableJson[key].name,
                voices: supportedSpeakableJson[key].voices.filter(v => !v.endsWith('1') && !v.endsWith('2') && !v.endsWith('3')),
                speakKey: key,
                translationKey: translationKey
            });
        } else if ('zh-Hant' === translationKey) {
            const key = 'zh-HK';
            translatableAndSpeakable.push({
                name: supportedSpeakableJson[key].name,
                voices: supportedSpeakableJson[key].voices.filter(v => !v.endsWith('1') && !v.endsWith('2') && !v.endsWith('3')),
                speakKey: key,
                translationKey: translationKey
            });

            /* } else if ('yue' === translationKey) {
                 const key = 'yue-CN';
                 translatableAndSpeakable.push({
                     name: supportedSpeakableJson[key].name,
                     voices: supportedSpeakableJson[key].voices,//.filter(v => !v.endsWith('1') && !v.endsWith('2') && !v.endsWith('3')),
                     speakKey: key,
                     translationKey: translationKey
                 });*/
        } else {

            //language that can be spoken
            supportedSpeakable
                .filter(key => key.startsWith(translationKey + '-'))
                .forEach(supportedSpeakableKey => {
                    // console.log("L (s)", speakRecognitionLanguages[recognitionKey], recognitionKey);
                    const supportedVoices = supportedSpeakableJson[supportedSpeakableKey].voices.filter(v => !v.endsWith('1') && !v.endsWith('2') && !v.endsWith('3'));

                    if (supportedVoices.length !== 0) {
                        // if(text2SpeechKey.includes("en"))
                        //     console.log("L (sp)", text2SpeechLanguages[text2SpeechKey], text2SpeechKey, supportedVoices);
                        translatableAndSpeakable.push({
                            name: supportedSpeakableJson[supportedSpeakableKey].name,
                            voices: supportedVoices,
                            speakKey: supportedSpeakableKey,
                            translationKey: translationKey
                        });
                    }
                })
        }
    });

    return {a: recognizableAndTranslatable, b: translatableAndSpeakable};

}


/*
export const buildLanguageMatrix = async () => {
    //Getting raw data
    const uri = 'https://api.cognitive.microsofttranslator.com/Languages?api-version=3.0&scope=translation';
    const response: RawTranslationL = await fetch(uri, {method: 'get'})
        .then(response => response.json())
        .catch(error => {
            console.log('--> error:', error);
            return {translation: new Map<String, Language>()}
        });

    //The ones that need to be limited
    const speakRecognitionLanguages: RawSpeakRecognitionL = untypedSpeakRecognitionLanguages; //"am-ET": "Amharic (Ethiopia)"
    const text2SpeechLanguages: RawText2SpeechL = untypedText2SpeechLanguages; //"am-ET": {"label":"Amharic (Ethiopia)", "voices": ["am-ET-MekdesNeural2","am-ET-AmehaNeural2"]},
    //key arrays!
    const speakRecognitionLanguageKeys = Object.keys(speakRecognitionLanguages);//["am-ET", ...]
    const text2SpeechLanguageKeys = Object.keys(text2SpeechLanguages);//["am-ET", ...]


    //The one that limits!
    const translationLanguages = response.translation;
    //"am":{"name":"Amharic","nativeName":"አማርኛ","dir":"ltr"}


    //Build maps
    const recognizableAndTranslatable: RecognizableAndTranslatableLanguage[] = [];
    const translatableAndSpeakable: TranslatableAndSpeakableLanguage[] = [];
    Object.keys(translationLanguages)
        .forEach(translationKey => {
            //"am" will never natch with "am-ET" => can not use => xxxxLanguageKeys.includes(translationKey)

            //language that can be recognized
            speakRecognitionLanguageKeys
                .filter(key => key.startsWith(translationKey + '-'))
                .forEach(recognitionKey => {
                    // console.log("L (t)", translationLanguages[translationKey], translationKey);
                    // console.log("L (s)", speakRecognitionLanguages[recognitionKey], recognitionKey);
                    recognizableAndTranslatable.push({
                        //toda la info del translate (name y nativeName) - nativeName se usa en el dropDown
                        ...translationLanguages[translationKey],
                        name: speakRecognitionLanguages[recognitionKey],
                        recognitionKey: recognitionKey,
                        translationKey: translationKey
                    } as RecognizableAndTranslatableLanguage);
                });

            //language that can be spoken
            text2SpeechLanguageKeys
                .filter(key => key.startsWith(translationKey + '-'))
                .forEach(text2SpeechKey => {
                    // console.log("L (s)", speakRecognitionLanguages[recognitionKey], recognitionKey);
                    const supportedVoices = text2SpeechLanguages[text2SpeechKey].voices.filter(v => !v.endsWith('1') && !v.endsWith('2') && !v.endsWith('3'));
                    // if(text2SpeechKey.includes("en"))
                    //     console.log("L (sp)", text2SpeechLanguages[text2SpeechKey], text2SpeechKey, supportedVoices);
                    translatableAndSpeakable.push({
                        name: text2SpeechLanguages[text2SpeechKey].name,
                        voices: supportedVoices,
                        speakKey: text2SpeechKey,
                        translationKey: translationKey
                    });
                })
        });

    return {a: recognizableAndTranslatable, b: translatableAndSpeakable};
}
*/


export const getDevices = async (kind: MediaDeviceKind) => {
    await navigator.mediaDevices.getUserMedia({audio: true});
    let any = await navigator.mediaDevices.getSupportedConstraints();
    // console.log(any)
    return await navigator.mediaDevices
        .enumerateDevices()
        .then((devices) => {
            // console.log(devices)
            return devices.filter(d => kind === d.kind && d.deviceId !== 'default');
        })
        .catch((err) => {
            console.error(`${err.name}: ${err.message}`);
            return [];
        });
}
